import { useEffect } from 'react';

const BASE_TITLE = 'Devotion Fund | Christian Fundraising for Missionaries & Volunteers';
const SHORT_BRAND = 'Devotion Fund';
const SITE_ORIGIN = 'https://devotionfund.com';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export interface PageMetaOptions {
  /** Absolute path for canonical + og:url, e.g. "/about". Defaults to current pathname. */
  path?: string;
  /** Absolute or root-relative OG image URL. Defaults to the site OG image. */
  image?: string;
}

/**
 * Sets document title, meta description, canonical URL, and Open Graph / Twitter
 * tags for the current page. Helps JS-rendering crawlers (Google, AI search) and
 * social/link previews resolve correct per-route metadata.
 *
 * @param pageTitle   For the homepage pass undefined/empty; otherwise e.g. "About".
 * @param description Meta description (optional; omit to leave description unchanged).
 * @param options     Optional canonical path + OG image overrides.
 */
export function usePageMeta(
  pageTitle: string | undefined,
  description?: string,
  options?: PageMetaOptions
) {
  const path = options?.path;
  const image = options?.image;

  useEffect(() => {
    // Google truncates (and often outright rewrites) a <title> past ~60 chars.
    // The page's own TITLE already carries the distinctive, click-worthy part
    // (and on comparison pages already says "Devotion Fund") -- appending the
    // full homepage tagline pushed every resource-page title past 150 chars,
    // guaranteeing the differentiator got cut from the SERP snippet (DF-322).
    const fullTitle = pageTitle
      ? pageTitle.toLowerCase().includes('devotion fund')
        ? pageTitle
        : `${pageTitle} | ${SHORT_BRAND}`
      : BASE_TITLE;
    document.title = fullTitle;

    const canonicalPath = path ?? window.location.pathname;
    const canonicalUrl = `${SITE_ORIGIN}${
      canonicalPath === '/' ? '/' : canonicalPath.replace(/\/$/, '')
    }`;
    setCanonical(canonicalUrl);

    const ogImage = image
      ? image.startsWith('http')
        ? image
        : `${SITE_ORIGIN}${image}`
      : DEFAULT_OG_IMAGE;

    setMeta('property', 'og:title', fullTitle);
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:image', ogImage);

    if (description !== undefined) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
  }, [pageTitle, description, path, image]);
}
